import React from 'react';
import { ROLE, ROLEALL, USERROLE, USER, USERALL } from "../../services/actions";
import { useDispatch, useSelector } from "react-redux";
import { 
    Dialog, DialogTitle, DialogContent, DialogActions, 
    Button, Box, Typography, Chip, FormControl, InputLabel, 
    Select, MenuItem, Checkbox, ListItemText, OutlinedInput, 
    Container, Grid, Paper, Stack, IconButton 
} from '@mui/material';
import { BsKeyFill, BsPlusLg, BsPersonBadgeFill, BsCheckCircleFill, BsShieldLockFill } from "react-icons/bs";
import Swal from 'sweetalert2';
import SearchableList from '../../libs/inputs/SearchableList';
import { FormHeroHeader, FormSectionCard } from "../extra/FormLayout";

export default function UserRole() {
    const dispatch = useDispatch();
    const roleState = useSelector((state) => state?.roleState);
    const roleallState = useSelector((state) => state?.roleallState);
    const userState = useSelector((state) => state?.userState);
    const userallState = useSelector((state) => state?.userallState);

    const [selectedRole, setSelectedRole] = React.useState([]);
    const [selectedUser, setSelectedUser] = React.useState(null);
    const [openDlg, setOpenDlg] = React.useState(false);

    React.useEffect(() => {
        dispatch({ type: `${ROLE}_GET`, payload: 0 });
        dispatch({ type: `${ROLEALL}_GET`, payload: 0 });
        dispatch({ type: `${USER}_GET`, payload: 0 });
        dispatch({ type: `${USERALL}_GET`, payload: 'public/user/0/?filter_by={"is_active":true}' });
    }, [dispatch]);

    const allRoles = React.useMemo(() => {
        return Array.isArray(roleallState?.data) ? roleallState.data : 
               (roleallState?.data?.results || roleState?.data?.data?.results || roleState?.data?.results || []);
    }, [roleallState, roleState]);

    const allUsers = React.useMemo(() => {
        return Array.isArray(userallState?.data) ? userallState.data : 
               (userallState?.data?.results || userState?.data?.data?.results || userState?.data?.results || []);
    }, [userallState, userState]);

    const handleOpenAssignModal = (user) => {
        setSelectedUser(user);
        let userRoleNames = [];
        if (Array.isArray(user?.group) && user.group.length > 0) {
            userRoleNames = user.group.map(g => typeof g === 'object' ? g.name : g);
        } else if (Array.isArray(user?.group_list) && user.group_list.length > 0) {
            userRoleNames = user.group_list.map(g => typeof g === 'object' ? g.name : g);
        } else if (Array.isArray(user?.groups) && user.groups.length > 0) {
            userRoleNames = user.groups.map(g => typeof g === 'object' ? g.name : g);
        }
        if (userRoleNames.length === 0 && user?.main_role && user.main_role !== "None") {
            userRoleNames = [user.main_role];
        }

        if (allRoles.length > 0 && userRoleNames.length > 0) {
            const currentRoles = allRoles.filter(r => 
                userRoleNames.includes(r.name) || userRoleNames.includes(r.id)
            );
            setSelectedRole(currentRoles);
        } else {
            setSelectedRole([]);
        }
        setOpenDlg(true);
    };

    const handleRoleChange = (event) => {
        const {
            target: { value },
        } = event;
        setSelectedRole(
            typeof value === 'string' ? value.split(',') : value,
        );
    };

    const assignRoleToUser = () => {
        if (!selectedUser) return;
        const data = {
            id: selectedUser.id,
            groups: (selectedRole || []).map(role => (typeof role === 'object' ? role?.name : role)).filter(Boolean),
        };
        dispatch({
            type: `${USERROLE}_PATCH`, payload: data, callback: (resp) => {
                dispatch({ type: `${USER}_GET`, payload: 0 });
                dispatch({ type: `${USERALL}_GET`, payload: 'public/user/0/?filter_by={"is_active":true}' });
                setOpenDlg(false);
                Swal.fire({
                    title: 'Roles Updated!',
                    text: `Assigned roles to ${selectedUser.first_name || 'user'} successfully.`,
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
            }
        });
    };

    const itemTemplate = (option) => {
        let rolesList = [];
        if (Array.isArray(option.group) && option.group.length > 0) {
            rolesList = option.group.map(g => typeof g === 'object' ? g : { name: g, id: g });
        } else if (Array.isArray(option.group_list) && option.group_list.length > 0) {
            rolesList = option.group_list.map(g => typeof g === 'object' ? g : { name: g, id: g });
        } else if (Array.isArray(option.groups) && option.groups.length > 0) {
            rolesList = option.groups.map(g => typeof g === 'object' ? g : { name: g, id: g });
        }
        if (rolesList.length === 0 && option.main_role && option.main_role !== "None") {
            rolesList = [{ name: option.main_role, id: option.main_role }];
        }

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', py: 1.2, px: 2, borderBottom: '1px solid #f1f5f9', '&:hover': { bgcolor: '#f8fafc' }, transition: 'all 0.2s' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
                    <Box sx={{ p: 1, bgcolor: '#e0e7ff', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <BsPersonBadgeFill size={20} color="#4f46e5" />
                    </Box>
                    <Box>
                        <Typography sx={{ fontWeight: 650, color: '#1e293b', fontSize: '14.5px' }}>
                            {option.first_name} {option.last_name || ''}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                            {option.email || option.username || `User ID: #${option.id}`}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center', flexWrap: 'wrap', mx: 2 }}>
                    {rolesList.length > 0 ? (
                        rolesList.map(group => (
                            <Chip 
                                key={group.id || group.name} 
                                label={group.name} 
                                size="small" 
                                sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 650, borderRadius: '6px', border: '1px solid #bbf7d0' }}
                            />
                        ))
                    ) : (
                        <Chip label="No Roles Assigned" size="small" sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 600, borderRadius: '6px' }} />
                    )}
                </Box>

                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<BsKeyFill />}
                    onClick={(e) => { e.stopPropagation(); handleOpenAssignModal(option); }}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', borderColor: '#cbd5e1', color: '#475569', '&:hover': { bgcolor: '#f1f5f9', borderColor: '#94a3b8' } }}
                >
                    Assign Roles
                </Button>
            </Box>
        );
    };

    return (
        <Box sx={{ py: 3, px: { xs: 1.5, md: 3 }, maxWidth: '1400px', mx: 'auto' }}>
            <Dialog 
                open={openDlg} 
                onClose={() => setOpenDlg(false)} 
                maxWidth="sm" 
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 1, bgcolor: '#e0e7ff', borderRadius: '10px', display: 'flex' }}>
                        <BsShieldLockFill size={22} color="#4f46e5" />
                    </Box>
                    Assign Multiple Roles to {selectedUser?.first_name || "User"}
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Typography sx={{ fontSize: '13px', color: '#64748b', mb: 3 }}>
                        Select one or more roles below to grant aggregate security permissions and module access to this employee.
                    </Typography>

                    <FormControl fullWidth>
                        <InputLabel id="multiple-role-select-label">Select Security Roles</InputLabel>
                        <Select
                            labelId="multiple-role-select-label"
                            id="multiple-role-select"
                            multiple
                            value={selectedRole || []}
                            onChange={handleRoleChange}
                            input={<OutlinedInput label="Select Security Roles" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip key={value.id || value.name} label={value.name} color="primary" size="small" sx={{ fontWeight: 650 }} />
                                    ))}
                                </Box>
                            )}
                        >
                            {allRoles.map((role) => {
                                const isChecked = (selectedRole || []).some(r => r.id === role.id || r.name === role.name);
                                return (
                                    <MenuItem key={role.id || role.name} value={role} sx={{ borderRadius: '8px', mx: 1, my: 0.2 }}>
                                        <Checkbox checked={isChecked} color="primary" />
                                        <ListItemText primary={role.name} primaryTypographyProps={{ fontWeight: isChecked ? 700 : 400 }} />
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
                    <Button onClick={() => setOpenDlg(false)} sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b' }}>
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={assignRoleToUser}
                        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 3, background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)' } }}
                    >
                        Save & Assign Roles
                    </Button>
                </DialogActions>
            </Dialog>

            <FormHeroHeader
                icon={<BsShieldLockFill size={26} />}
                title="Employee Role Assignment"
                subtitle="Assign multiple security roles to employees to grant combined permissions and system capabilities."
                badgeText="Multi-Role Assignment"
            />

            <FormSectionCard
                icon={<BsPersonBadgeFill size={20} />}
                title="Employee Roles Directory"
                subtitle="Search for an employee and click 'Assign Roles' to update their access tiers"
            >
                <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', bgcolor: 'white' }}>
                    <SearchableList
                        onClick={(i, data) => handleOpenAssignModal(data)}
                        optionLabel="first_name"
                        list={allUsers}
                        onRowRender={itemTemplate}
                    />
                </Box>
            </FormSectionCard>
        </Box>
    );
}